'use client'
import Image from 'next/image'
import { useState } from 'react'

import { asText } from '@prismicio/client'
import { PrismicNextImage } from '@prismicio/next'
import * as Accordion from '@radix-ui/react-accordion'
import { PrismicLink, PrismicRichText } from '@prismicio/react'

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

// Helper to get text from rich text field
const getTitleText = (titleField: any): string => {
  return asText(titleField) || ''
}

const CmmcProductDefenseComponent = ({ data }: { data: DataObject }) => {
  const accordionContents = data.primary.accordion_content || []
  // Track active accordion index (default to first item)
  const [activeIndex, setActiveIndex] = useState<number>(0)

  return (
    <section className="bg-primary-ink-100 py-20 lg:py-[120px]">
      <div className="container">
        {/* Header */}
        <div className="mx-auto mb-16 max-w-5xl text-center">
          <PrismicRichText
            field={data.primary.section_title}
            components={{
              heading2: ({ children }) => <h2 className="text-white">{children}</h2>,
            }}
          />

          <PrismicRichText
            field={data.primary.section_description}
            components={{
              paragraph: ({ children }) => (
                <p className="mt-14 text-base font-normal leading-[140%] tracking-[1%] text-white/90 lg:text-[22px]">
                  {children}
                </p>
              ),
            }}
          />
        </div>

        {/* Accordion Contents with Image */}
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Left: Accordion */}
          <div>
            <Accordion.Root
              type="single"
              collapsible
              defaultValue="0"
              onValueChange={(value) => setActiveIndex(value ? parseInt(value) : 0)}
              className="flex flex-col gap-3">
              {accordionContents.map((accordionContent: ItemObject, index: number) => {
                const titleText = getTitleText(accordionContent.title)
                return (
                  <Accordion.Item
                    key={index}
                    value={String(index)}
                    className="group rounded-md border border-transparent bg-[#2E3C43] shadow-dark transition-colors duration-300 data-[state=open]:border-primary-green-85 data-[state=open]:bg-[#535E65]">
                    <Accordion.Header>
                      <Accordion.Trigger className="flex w-full items-center justify-between px-6 py-5 text-left">
                        <span className="text-base font-semibold leading-[28px] tracking-wider text-white lg:text-[20px]">
                          {titleText}
                        </span>
                        <div className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center">
                          {/* Plus icon (shown when closed) */}
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute transition-opacity duration-300 group-data-[state=open]:opacity-0">
                            <path
                              d="M5 6.66667H0V5H5V0H6.66667V5H11.6667V6.66667H6.66667V11.6667H5V6.66667Z"
                              fill="white"
                            />
                          </svg>

                          {/* Minus icon (shown when open) */}
                          <svg
                            width="12"
                            height="2"
                            viewBox="0 0 12 2"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute opacity-0 transition-opacity duration-300 group-data-[state=open]:opacity-100">
                            <path d="M0 1.66667V0H11.6667V1.66667H0Z" fill="white" />
                          </svg>
                        </div>
                      </Accordion.Trigger>
                    </Accordion.Header>

                    <Accordion.Content className="overflow-hidden transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div className="px-6 pb-5">
                        <PrismicRichText
                          field={accordionContent.description}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="text-base font-normal leading-[140%] tracking-[1%] text-white lg:text-[22px]">
                                {children}
                              </p>
                            ),
                          }}
                        />

                        <div className="relative mt-10 block w-full max-w-lg overflow-hidden lg:hidden">
                          <PrismicNextImage
                            field={accordionContent.image}
                            className="h-auto w-full rounded-md object-cover"
                            fallbackAlt=""
                          />
                        </div>
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                )
              })}
            </Accordion.Root>

            {/* CTA Button */}
            {data.primary.section_btn_link && (
              <div className="mt-10">
                <PrismicLink
                  field={data.primary.section_btn_link}
                  className="btn btn-default btn-interaction btn-white inline-flex items-center">
                  <span className="mr-2">{data.primary.section_btn_link?.text}</span>
                  <Image src="/images/angle-right-navy.svg" width={20} height={16} alt="" aria-hidden="true" />
                </PrismicLink>
              </div>
            )}
          </div>

          {/* Right: Image - changes based on active accordion index. No aspect-square so container matches image ratio = full width + height, no crop. */}
          <div className="relative hidden w-full max-w-lg overflow-hidden lg:block">
            {accordionContents[activeIndex]?.image?.url && (
              <PrismicNextImage
                field={accordionContents[activeIndex].image}
                className="h-auto w-full rounded-md object-cover"
                fallbackAlt=""
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CmmcProductDefenseComponent
